import type { TemplateAnnotation } from '@/api';
import { parseBlockTag } from '@/composables/contractGeneration/useTemplateAnnotations';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaFonts,
  gaiaNeutral,
  gaiaStatus,
  gaiaTemplating,
  gaiaTracking,
} from '@/components/design/themes/tokens';

/**
 * What the tag-settings form is configuring, in the author's words — shared
 * by the drawer header (Word add-in, snippet library) and the embedded form
 * on the Fields page.
 */
export function annotationConfigTitle(annotation: TemplateAnnotation | null | undefined): string {
  if (!annotation) return 'Configure field';
  const kind = annotation.kind ?? 'inline';
  if (kind === 'block_end') return 'Section end';
  if (kind === 'block_middle') {
    return parseBlockTag(annotation).keyword === 'else' ? 'Otherwise branch' : 'Else-if branch';
  }
  if (kind === 'block_start') {
    return parseBlockTag(annotation).keyword === 'for' ? 'Repeat section' : 'Conditional section';
  }
  if (annotation.binding?.kind === 'entity') return 'Configure role field';
  if (annotation.binding?.kind === 'object') return 'Configure group field';
  return 'Configure field';
}

/**
 * The form's tokens as inline custom properties. The drawer teleports to
 * <body>, where component-root `v-bind()` vars never arrive, so both hosts
 * bind this map on the element that wraps the form.
 */
export const annotationConfigStyleVars = {
  '--tplcfg-fs-meta': gaiaFontSize.meta,
  '--tplcfg-fs-nano': gaiaFontSize.nano,
  '--tplcfg-fw-regular': String(gaiaFontWeight.regular),
  '--tplcfg-fw-medium': String(gaiaFontWeight.medium),
  '--tplcfg-fw-semibold': String(gaiaFontWeight.semibold),
  '--tplcfg-fw-bold': String(gaiaFontWeight.bold),
  '--tplcfg-track-caps': gaiaTracking.labelSoft,
  '--tplcfg-track-block': gaiaTracking.blockTag,
  '--tplcfg-mono': gaiaFonts.mono,
  '--tplcfg-fs-row': gaiaFontSize.row,
  '--tplcfg-surface': gaiaNeutral.surface,
  '--tplcfg-soft': gaiaNeutral.surfaceSoft,
  '--tplcfg-muted': gaiaNeutral.surfaceMuted,
  '--tplcfg-border': gaiaNeutral.borderCard,
  '--tplcfg-faint': gaiaNeutral.textFaint,
  '--tplcfg-subtle': gaiaNeutral.textSubtle,
  '--tplcfg-text': gaiaNeutral.text,
  '--tplcfg-ink': gaiaNeutral.ink,
  '--tplcfg-accent': gaiaTemplating.customFg,
  '--tplcfg-accent-strong': gaiaAccent.strong,
  '--tplcfg-accent-border': gaiaTemplating.customBorder,
  '--tplcfg-accent-tint': gaiaTemplating.customSurface,
  '--tplcfg-accent-wash': gaiaAccent.wash,
  '--tplcfg-entity-border': gaiaTemplating.entityBorder,
  '--tplcfg-entity-fg': gaiaTemplating.entityFg,
  '--tplcfg-block-fg': gaiaTemplating.blockFg,
  '--tplcfg-block-surface': gaiaTemplating.blockSurface,
  '--tplcfg-alert': gaiaStatus.alert,
  '--tplcfg-alert-surface': gaiaStatus.alertSurface,
  '--tplcfg-alert-border': gaiaStatus.alertBorder,
};
